import React from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatIDR, formatDate, INVOICE_STATUS } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Download } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const [inv, setInv] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [pay, setPay] = React.useState({ amount: 0, method: "transfer", reference: "", notes: "" });

  const load = React.useCallback(async () => { const { data } = await api.get(`/invoices/${id}`); setInv(data); setPay((p) => ({ ...p, amount: data.outstanding })); }, [id]);
  React.useEffect(() => { load(); }, [load]);

  const submitPayment = async () => {
    try {
      await api.post("/payments", { invoice_id: id, ...pay, amount: +pay.amount });
      toast.success("Pembayaran dicatat");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!inv) return <div className="text-sm text-neutral-500">Memuat…</div>;

  return (
    <div className="space-y-4" data-testid="invoice-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-neutral-500">Invoice</div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            {inv.number} <StatusBadge statusMap={INVOICE_STATUS} value={inv.status} className="ml-2" />
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-invoice-pdf"><Download className="h-4 w-4 mr-1" /> Unduh PDF</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={inv.outstanding <= 0} data-testid="btn-record-payment">Catat Pembayaran</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Catat Pembayaran</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Jumlah</Label><Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount" /></div>
                <div><Label>Metode</Label>
                  <select className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} data-testid="payment-method">
                    <option value="transfer">Bank Transfer</option>
                    <option value="cash">Tunai</option>
                    <option value="card">Kartu Kredit</option>
                    <option value="gateway">Payment Gateway</option>
                    <option value="other">Lainnya</option>
                  </select>
                </div>
                <div><Label>Referensi</Label><Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></div>
                <div><Label>Catatan</Label><Input value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button onClick={submitPayment} className="bg-green-600 hover:bg-green-700" data-testid="submit-payment">Simpan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="card-flat p-6 max-w-4xl">
        <div className="flex justify-between border-b border-neutral-200 pb-4 mb-4">
          <div>
            <div className="font-bold text-lg" style={{ fontFamily: "Manrope, sans-serif" }}>Grippel Rental</div>
            <div className="text-xs text-neutral-500">Jakarta · info@grippelrental.com</div>
          </div>
          <div className="text-right text-sm">
            <div className="text-xs uppercase text-neutral-500">Invoice</div>
            <div className="font-bold text-lg">{inv.number}</div>
            <div className="text-xs text-neutral-500">Diterbitkan {formatDate(inv.issue_date)}</div>
            <div className="text-xs text-neutral-500">Jatuh Tempo {formatDate(inv.due_date)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <div className="text-xs uppercase text-neutral-500">Kepada</div>
            <div className="font-medium">{inv.customer?.name}</div>
            <div className="text-xs text-neutral-500">{inv.customer?.billing_address}</div>
            {inv.customer?.npwp && <div className="text-xs text-neutral-500">NPWP: {inv.customer.npwp}</div>}
          </div>
        </div>
        <table className="w-full data-table mb-4">
          <thead><tr><th className="text-left">Equipment</th><th className="text-right">Qty</th><th className="text-right">Hari</th><th className="text-right">Harga</th><th className="text-right">Subtotal</th></tr></thead>
          <tbody>
            {inv.items?.map((it, i) => (
              <tr key={i}>
                <td>{it.product_name || "-"}</td>
                <td className="text-right">{it.qty}</td>
                <td className="text-right">{it.days}</td>
                <td className="text-right">{formatIDR(it.unit_price)}</td>
                <td className="text-right">{formatIDR(it.qty * it.days * it.unit_price - (it.discount || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end">
          <div className="w-80 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span>{formatIDR(inv.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">PPN {inv.tax_rate}%</span><span>{formatIDR(inv.tax)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Deposit</span><span>{formatIDR(inv.deposit)}</span></div>
            <div className="flex justify-between font-bold text-base border-t border-green-600 pt-2">
              <span>Grand Total</span><span className="text-green-700">{formatIDR(inv.total)}</span>
            </div>
            <div className="flex justify-between text-green-700"><span>Sudah Dibayar</span><span>{formatIDR(inv.amount_paid)}</span></div>
            <div className="flex justify-between font-bold text-red-700"><span>Sisa Tagihan</span><span>{formatIDR(inv.outstanding)}</span></div>
          </div>
        </div>

        {inv.payments?.length > 0 && (
          <div className="mt-6 border-t border-neutral-200 pt-4">
            <div className="text-sm font-semibold mb-2">Riwayat Pembayaran</div>
            <table className="w-full data-table">
              <thead><tr><th className="text-left">Tgl</th><th className="text-left">Metode</th><th className="text-left">Ref</th><th className="text-right">Jumlah</th></tr></thead>
              <tbody>
                {inv.payments.map((p) => (
                  <tr key={p.id}><td className="text-xs">{formatDate(p.date)}</td><td className="text-xs capitalize">{p.method}</td><td className="text-xs">{p.reference || "-"}</td><td className="text-right font-medium">{formatIDR(p.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
